import { createAsyncThunk } from '@reduxjs/toolkit';
import { FileFolderType, ITab, ITabFlags, ModalType } from '@qodly/shared';
import {
  getFileContent,
  IFileContentResponse,
  ISetFileContentResponse,
  setFileContent,
  ROLES_TAB_PATH,
} from '@qodly/shared';
import { AppState } from '..';
import { openModal } from '../modals/thunks';
import { selectTabByReference } from '../root';
import { toAPI, sanitize } from './adapter';
import { ISanitized, TRawData } from './types';

const getRoles = () =>
  getFileContent<TRawData>({
    name: ROLES_TAB_PATH,
  });

const rolesTabName = 'Roles & Privileges';

export const fetchRoles = createAsyncThunk<
  IFileContentResponse<ISanitized>,
  void,
  {
    state: AppState;
    rejectValue: string;
  }
>('roles/fetchRoles', async (_payload, { dispatch, rejectWithValue }) => {
  let roles: IFileContentResponse<TRawData>;
  try {
    // Get roles from API
    roles = await getRoles();
  } catch (e: any) {
    if (!Array.isArray(e?.data?.__ERROR)) {
      return rejectWithValue('Error while loading the roles');
    }

    roles = {
      name: ROLES_TAB_PATH,
      date: new Date().toISOString(),
      content: {
        permissions: {
          allowed: [],
        },
        privileges: [],
        roles: [],
      },
    };
  }

  const tab: ITab = {
    name: rolesTabName,
    type: FileFolderType.ROLES,
    date: roles.date,
    view: {
      panel: {
        isOpen: false,
        type: '',
        current: '',
      },
    },
    path: ROLES_TAB_PATH,
    flags: {},
    extra: {
      editor: {
        key: 'editors:roles',
      },
    },
    content: {},
  };

  dispatch(selectTabByReference(tab));

  return {
    ...roles,
    content: sanitize(roles.content),
  };
});

export const fetchRolesChanges = createAsyncThunk<
  ITabFlags,
  void,
  { state: AppState }
>('roles/fetchRolesChanges', async (_payload, { getState }) => {
  const { tabs } = getState().root;
  const rolesTab = tabs.find((tab) => tab.path === ROLES_TAB_PATH);

  if (rolesTab) {
    const { date } = getState().roles;
    if (date) {
      const outdated = await isRolesOutdated(date);
      if (outdated) {
        return {
          old: true,
        };
      }
    }
  }

  return {};
});

export const isRolesOutdated = async (date: string | undefined) => {
  if (!date) return false;

  try {
    const roles = await getRoles();

    if (roles.date) {
      const oldRolesDate = new Date(date!);
      const newRolesDate = new Date(roles.date);
      if (oldRolesDate > newRolesDate) {
        return true;
      }
    }
  } catch (e: any) {
    if (!Array.isArray(e?.data.__ERROR)) {
      throw e;
    }
  }

  return false;
};

export const saveRoles = createAsyncThunk<
  ISetFileContentResponse,
  ISanitized,
  {
    state: AppState;
    rejectValue: string | ITabFlags;
  }
>(
  'roles/saveRoles',
  async (content, { getState, rejectWithValue, dispatch }) => {
    try {
      // Check if roles are outdated before saving
      const { flags, date } = getState().roles;

      const outdated = flags.old || (await isRolesOutdated(date));

      if (outdated) {
        const { confirmed } = await dispatch(
          openModal({
            title: rolesTabName,
            message:
              'New content has been found on the server. Are you sure you want to continue?',
            type: ModalType.CONFIRM,
            icon: 'FdSave',
          }),
        ).unwrap();

        if (!confirmed) {
          return rejectWithValue({ old: true });
        }
      }

      // Save roles
      const apiResponse = await setFileContent({
        name: ROLES_TAB_PATH,
        content: toAPI(content),
      });

      return apiResponse;
    } catch (e) {
      return rejectWithValue('Error while loading the roles');
    }
  },
);
